import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Luggage, Download, X } from 'lucide-react'
import { useTrips } from './useTrips'
import { TripCard } from '../../shared/components/TripCard'
import { EmptyState } from '../../shared/components/EmptyState'
import { Spinner } from '../../shared/components/Spinner'
import { Button } from '../../shared/components/Button'
import { useInstallPrompt } from '../../shared/hooks/useInstallPrompt'
import { deriveTripPhase, type TripPhase } from '../../shared/hooks/useTripPhase'
import type { Trip } from '../../shared/api/types'

const SECTIONS: Array<{ phase: TripPhase; title: string }> = [
  { phase: 'live', title: 'Live now' },
  { phase: 'upcoming', title: 'Upcoming' },
  { phase: 'planning', title: 'Drafts' },
  { phase: 'completed', title: 'Past trips' },
]

export function Dashboard() {
  const { data: trips, isLoading, isError } = useTrips()
  const { canInstall, promptInstall } = useInstallPrompt()
  const [installDismissed, setInstallDismissed] = useState(false)

  return (
    <div className="mx-auto max-w-6xl">
      {canInstall && !installDismissed && (
        <div className="mb-5 flex items-center gap-3 rounded-st-lg border border-st-line bg-st-surface p-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
            <Download className="h-4.5 w-4.5" />
          </span>
          <p className="min-w-0 flex-1 text-sm text-st-text">
            Install Smart Travel for quick access and offline trip details.
          </p>
          <Button variant="outline" onClick={() => void promptInstall()}>
            Install
          </Button>
          <button
            type="button"
            aria-label="Dismiss install prompt"
            onClick={() => setInstallDismissed(true)}
            className="rounded-st-sm p-1.5 text-st-muted hover:bg-st-surface-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-st-text">Your trips</h1>
          <p className="mt-1 text-sm text-st-muted">Plan, live, and remember every journey.</p>
        </div>
        <Link to="/trips/new">
          <Button variant="accent">
            <Plus className="h-4 w-4" /> Create trip
          </Button>
        </Link>
      </div>

      {isLoading && (
        <div className="py-12">
          <Spinner label="Loading trips…" />
        </div>
      )}

      {isError && (
        <EmptyState title="Couldn't load your trips" description="Please refresh and try again." />
      )}

      {trips && trips.length === 0 && (
        <EmptyState
          Icon={Luggage}
          title="No trips yet"
          description="Start your first journey — add destinations, days, and places."
          action={
            <Link to="/trips/new">
              <Button variant="accent">
                <Plus className="h-4 w-4" /> Create your first trip
              </Button>
            </Link>
          }
        />
      )}

      {trips && trips.length > 0 && (
        <div className="space-y-8">
          {SECTIONS.map(({ phase, title }) => {
            const inSection = trips.filter((t) => deriveTripPhase(t) === phase)
            if (inSection.length === 0) return null
            return <Section key={phase} title={title} trips={inSection} />
          })}
        </div>
      )}
    </div>
  )
}

function Section({ title, trips }: { title: string; trips: Trip[] }) {
  return (
    <section aria-label={title}>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-st-muted">{title}</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {trips.map((trip) => (
          <TripCard key={trip.id} trip={trip} />
        ))}
      </div>
    </section>
  )
}
